import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MessageCircle, Share2, Trash2, Image as ImageIcon } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import PostReactions from "@/components/PostReactions";
import CommentThread from "@/components/CommentThread";
import { linkify } from "@/components/ExpandableText";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** A page/group post with the same social actions as the main feed: reactions, comments (+replies, @mentions) and share. */
export default function PagePostCard({ page, post, onDelete }) {
  const [comments, setComments] = useState(post.comments || []);
  const [open, setOpen] = useState(false);
  const [shares, setShares] = useState(post.share_count || 0);

  const share = async () => {
    const url = `${window.location.origin}/pages/${page.page_id}?post=${post.post_id}`;
    try {
      if (navigator.share) await navigator.share({ title: page.name, text: (post.content || "").slice(0, 120), url });
      else { await navigator.clipboard.writeText(url); toast.success("Link copiado!"); }
      await axios.post(`${API}/pages/${page.page_id}/posts/${post.post_id}/share`);
      setShares((s) => s + 1);
    } catch (e) { if (e?.name !== "AbortError") toast.error("Não foi possível compartilhar"); }
  };

  return (
    <Card className="p-5" data-testid={`page-post-${post.post_id}`}>
      <div className="flex items-start gap-3 mb-3">
        <Avatar className="w-10 h-10"><AvatarImage src={post.author_picture} /><AvatarFallback>{post.author_name?.[0]}</AvatarFallback></Avatar>
        <div className="flex-1">
          <div className="font-semibold text-sm">{page.name}</div>
          <div className="text-[11px] text-muted-foreground">{new Date(post.created_at).toLocaleString("pt-BR")}</div>
        </div>
        {page.is_owner && (
          <button onClick={() => onDelete(post.post_id)} className="text-destructive hover:opacity-80" data-testid={`page-post-delete-${post.post_id}`} aria-label="Excluir">
            <Trash2 className="w-4 h-4" />
          </button>
        )}
      </div>
      {post.content && <p className="text-base whitespace-pre-wrap mb-3 break-words">{linkify(post.content, { mentions: post.mentions })}</p>}
      {post.image_url && <img src={post.image_url} alt="" className="w-full rounded-xl border border-border mb-2" />}
      {post.video_url && <video src={post.video_url} className="w-full rounded-xl border border-border max-h-[60vh]" controls playsInline />}
      {post.kind === "banner" && (
        <div className="mt-1 inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-primary font-bold"><ImageIcon className="w-3 h-3" /> Banner</div>
      )}

      <div className="flex items-center gap-1 sm:gap-2 mt-3 pt-3 border-t border-border flex-wrap" data-testid={`page-post-actions-${post.post_id}`}>
        <PostReactions postId={post.post_id} compact />
        <button type="button" onClick={() => setOpen((v) => !v)} className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground px-2 py-1 rounded-full hover:bg-muted transition-colors" data-testid={`page-post-comment-btn-${post.post_id}`}>
          <MessageCircle className="w-4 h-4" /> {comments.length > 0 ? comments.length : "Comentar"}
        </button>
        <button type="button" onClick={share} className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground px-2 py-1 rounded-full hover:bg-muted transition-colors" data-testid={`page-post-share-btn-${post.post_id}`}>
          <Share2 className="w-4 h-4" /> {shares > 0 ? shares : "Compartilhar"}
        </button>
      </div>

      {open && (
        <div className="mt-3" data-testid={`page-post-comments-${post.post_id}`}>
          <CommentThread
            postId={post.post_id}
            comments={comments}
            onNewComment={(c) => setComments((cs) => [...cs, c])}
            onLocalUpdate={(c) => setComments((cs) => cs.map((x) => (x.comment_id === c.comment_id ? c : x)))}
            onEdited={(c) => setComments((cs) => cs.map((x) => (x.comment_id === c.comment_id ? c : x)))}
            onDeleted={(id) => setComments((cs) => cs.filter((x) => x.comment_id !== id && x.parent_comment_id !== id))}
            inputTestId={post.post_id}
          />
        </div>
      )}
    </Card>
  );
}
